// Analytics 前端 hooks — Phase 6 Agent A
// 消费 /api/v1/analytics/{summary,timeseries,distribution,dormant-aliases}
// TanStack Query 分层（CLAUDE.md §四 前端 UX）：staleTime=60s / refetchInterval off
// —— 分析数据变化没那么快；用户改窗口/切 metric 时 queryKey 变化自然重取。

import { useQuery } from '@tanstack/react-query';

import { api } from './api.js';

import type {
  AnalyticsDimension,
  AnalyticsDistribution,
  AnalyticsMetric,
  AnalyticsSummary,
  AnalyticsTimeSeries,
} from '@aliashub/shared/dto';

export type {
  AnalyticsDimension,
  AnalyticsDistribution,
  AnalyticsDistributionItem,
  AnalyticsKpi,
  AnalyticsMetric,
  AnalyticsSummary,
  AnalyticsTimePoint,
  AnalyticsTimeSeries,
} from '@aliashub/shared/dto';

/** F61：时序粒度 —— 7 天固定 day；30/90 天可切 day/week/month */
export type AnalyticsGranularity = 'day' | 'week' | 'month';

/** F61：沉睡别名条目 —— 距上次收到邮件超过 N 天的活跃别名 */
export interface DormantAliasItem {
  aliasId: number;
  address: string;
  /** unix seconds，从未收到时为 null（此时视为"从注册起就沉睡"） */
  lastMessageAt: number | null;
  /** 距今天数（null lastMessageAt → 用 createdAt 计算） */
  daysSince: number;
}

export interface DormantAliasesResult {
  items: DormantAliasItem[];
  /** 查询用的阈值天数（回显给前端） */
  days: number;
}

const STALE = 60_000;

export function useAnalyticsSummary(days: number) {
  return useQuery<AnalyticsSummary>({
    queryKey: ['analytics', 'summary', days] as const,
    queryFn: () =>
      api.get<AnalyticsSummary>(`/api/v1/analytics/summary?days=${String(days)}`),
    staleTime: STALE,
  });
}

export function useAnalyticsTimeSeries(
  metric: AnalyticsMetric,
  days: number,
  granularity: AnalyticsGranularity = 'day',
) {
  return useQuery<AnalyticsTimeSeries>({
    queryKey: ['analytics', 'timeseries', metric, days, granularity] as const,
    queryFn: () =>
      api.get<AnalyticsTimeSeries>(
        `/api/v1/analytics/timeseries?metric=${metric}&days=${String(days)}&granularity=${granularity}`,
      ),
    staleTime: STALE,
  });
}

export function useAnalyticsDistribution(
  dim: AnalyticsDimension,
  days: number,
  limit: number,
) {
  return useQuery<AnalyticsDistribution>({
    queryKey: ['analytics', 'distribution', dim, days, limit] as const,
    queryFn: () =>
      api.get<AnalyticsDistribution>(
        `/api/v1/analytics/distribution?dim=${dim}&days=${String(days)}&limit=${String(limit)}`,
      ),
    staleTime: STALE,
  });
}

/** F61：沉睡别名 top N — /api/v1/analytics/dormant-aliases?days=&limit= */
export function useDormantAliases(days: number, limit = 20) {
  return useQuery<DormantAliasesResult>({
    queryKey: ['analytics', 'dormant-aliases', days, limit] as const,
    queryFn: () =>
      api.get<DormantAliasesResult>(
        `/api/v1/analytics/dormant-aliases?days=${String(days)}&limit=${String(limit)}`,
      ),
    staleTime: STALE,
  });
}

/** F61：某窗口下允许的粒度选项 */
export function granularitiesFor(days: number): AnalyticsGranularity[] {
  if (days <= 7) return ['day'];
  if (days <= 30) return ['day', 'week'];
  return ['day', 'week', 'month'];
}

// ==================== label helpers（UI 层用） ====================

export const METRIC_LABEL: Record<AnalyticsMetric, string> = {
  messages: '收件',
  forwarded: '转发',
  attachments: '附件',
  ai_summaries: 'AI 摘要',
};

export const DIM_LABEL: Record<AnalyticsDimension, string> = {
  alias: '别名',
  domain: '域名',
  from_domain: '发件域',
  label: '标签',
};

export const GRANULARITY_LABEL: Record<AnalyticsGranularity, string> = {
  day: '按天',
  week: '按周',
  month: '按月',
};

/** 字节数格式化 —— 与 attachments.ts 保持一致 */
export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  const unit = units[i] ?? 'B';
  return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${unit}`;
}
